/* eslint-disable react-hooks/exhaustive-deps, jsx-a11y/anchor-is-valid */
import { useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDown, ArrowRight, Layers, GitBranch,
  BarChart3, RefreshCw, ShieldCheck, Info, ExternalLink,
} from "lucide-react";
import "./ResearchPage.css";

/* ── Section metadata for navigation ── */
const SECTIONS = [
  { id: "rp-s1", label: "ECONOMICS" },
  { id: "rp-s2", label: "DOCUMENTATION" },
  { id: "rp-s3", label: "RESOLUTION" },
  { id: "rp-s4", label: "THE SHIFT", turning: true },
  { id: "rp-s5", label: "SMUSHLABS" },
  { id: "rp-s6", label: "ARCHITECTURE" },
  { id: "rp-s7", label: "CAPABILITIES" },
  { id: "rp-s8", label: "EVIDENCE" },
];

/* ── Footnote component ── */
function FootnoteRef({ text, children }: { text: string; children: React.ReactNode }) {
  const ref = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);

  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // close any other open tooltips
    document.querySelectorAll(".rp-fn-tooltip.show").forEach((el) => {
      if (el !== tipRef.current) el.classList.remove("show");
    });
    tipRef.current?.classList.toggle("show");
  };

  return (
    <span className="rp-fn-ref" ref={ref} onClick={toggle}>
      {children}
      <div className="rp-fn-tooltip" ref={tipRef}>{text}</div>
    </span>
  );
}

/* ── Bar component ── */
function Bar({ label, value, green }: { label: string; value: string; green?: boolean }) {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("animate"); }); },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 14, color: "#71717a", fontWeight: 300 }}>{label}</span>
        <span style={{ fontSize: 14, color: "#a1a1aa", fontFamily: "'JetBrains Mono', monospace" }}>{value}</span>
      </div>
      <div className="rp-bar-track">
        <div
          ref={barRef}
          className={`rp-bar-fill ${green ? "rp-bar-fill-green" : "rp-bar-fill-gray"}`}
          style={{ width: value }}
        />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════ */
/* ══  MAIN COMPONENT                 ══ */
/* ══════════════════════════════════════ */

export default function ResearchPage() {
  const wheelNavRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nodesRef = useRef<HTMLDivElement>(null);
  const mobileNavRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLElement>(null);
  const activeIndexRef = useRef(-1);

  /* ── Scroll to top on mount ── */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* ── Close tooltips on document click ── */
  useEffect(() => {
    const handler = () => {
      document.querySelectorAll(".rp-fn-tooltip.show").forEach((el) => el.classList.remove("show"));
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  /* ── Reveal animations ── */
  useEffect(() => {
    const els = document.querySelectorAll(".rp-reveal");
    const obs = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }); },
      { threshold: 0.08, rootMargin: "0px 0px -30px 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  /* ── Bezier helpers ── */
  const qBez = (t: number, a: number, b: number, c: number) => {
    const m = 1 - t; return m * m * a + 2 * m * t * b + t * t * c;
  };
  const qBezDeriv = (t: number, a: number, b: number, c: number) =>
    2 * (1 - t) * (b - a) + 2 * t * (c - b);

  const bezierLength = (p0x: number, p0y: number, p1x: number, p1y: number, p2x: number, p2y: number) => {
    let len = 0, px = p0x, py = p0y;
    for (let i = 1; i <= 200; i++) {
      const t = i / 200;
      const x = qBez(t, p0x, p1x, p2x), y = qBez(t, p0y, p1y, p2y);
      len += Math.sqrt((x - px) ** 2 + (y - py) ** 2);
      px = x; py = y;
    }
    return Math.max(len, 1);
  };

  const tForArcLen = (targetLen: number, _total: number, p0x: number, p0y: number, p1x: number, p1y: number, p2x: number, p2y: number) => {
    let len = 0, px = p0x, py = p0y;
    for (let i = 1; i <= 200; i++) {
      const t = i / 200;
      const x = qBez(t, p0x, p1x, p2x), y = qBez(t, p0y, p1y, p2y);
      len += Math.sqrt((x - px) ** 2 + (y - py) ** 2);
      px = x; py = y;
      if (len >= targetLen) return t;
    }
    return 1;
  };

  /* ── Wheel navigation ── */
  const initWheel = useCallback(() => {
    const canvas = canvasRef.current;
    const nodesContainer = nodesRef.current;
    const wheelNav = wheelNavRef.current;
    const mobileNav = mobileNavRef.current;
    const heroEl = heroRef.current;
    const ctaEl = ctaRef.current;
    if (!canvas || !nodesContainer || !wheelNav || !mobileNav || !heroEl || !ctaEl) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const headerHeight = 70;
    const h = Math.max(window.innerHeight - headerHeight, 320);
    const P0X = 60, P0Y = 50, P2X = 70, P2Y = h - 50;
    const P1X = P0X + 85, P1Y = h * 0.5;

    canvas.width = 210 * dpr;
    canvas.height = h * dpr;
    canvas.style.width = "210px";
    canvas.style.height = h + "px";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const totalLen = bezierLength(P0X, P0Y, P1X, P1Y, P2X, P2Y);

    // Create nodes
    nodesContainer.innerHTML = "";
    const mobileInner = mobileNav.querySelector(".rp-mobile-nav-inner");
    if (mobileInner) mobileInner.innerHTML = "";
    const elNodes: HTMLElement[] = [];
    const mobileChips: HTMLElement[] = [];

    for (let i = 0; i < SECTIONS.length; i++) {
      const sec = SECTIONS[i];

      // Desktop node
      const node = document.createElement("div");
      node.className = "rp-wheel-node" + (sec.turning ? " turning" : "");
      const dot = document.createElement("div");
      dot.className = "rp-node-dot";
      node.appendChild(dot);
      const connector = document.createElement("div");
      connector.className = "rp-node-connector";
      const line = document.createElement("div");
      line.className = "rp-node-line";
      connector.appendChild(line);
      const label = document.createElement("span");
      label.className = "rp-node-label";
      label.textContent = sec.label;
      connector.appendChild(label);
      node.appendChild(connector);

      const idx = i;
      node.addEventListener("click", () => {
        document.getElementById(SECTIONS[idx].id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      nodesContainer.appendChild(node);
      elNodes.push(node);

      // Position
      const target = SECTIONS.length === 1 ? totalLen * 0.5 : (i / (SECTIONS.length - 1)) * totalLen;
      const t = tForArcLen(target, totalLen, P0X, P0Y, P1X, P1Y, P2X, P2Y);
      const x = qBez(t, P0X, P1X, P2X);
      const y = qBez(t, P0Y, P1Y, P2Y);
      const dx = qBezDeriv(t, P0X, P1X, P2X);
      const dy = qBezDeriv(t, P0Y, P1Y, P2Y);
      const angle = Math.atan2(dy, dx);
      node.style.left = x + "px";
      node.style.top = y + "px";
      if (Math.abs(angle) > Math.PI / 2) {
        connector.style.flexDirection = "row-reverse";
        connector.style.paddingLeft = "0";
        connector.style.paddingRight = "4px";
      }

      // Mobile chip
      if (mobileInner) {
        const chip = document.createElement("span");
        chip.className = "rp-mob-chip";
        chip.textContent = sec.label;
        chip.addEventListener("click", () => {
          document.getElementById(SECTIONS[idx].id)?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
        mobileInner.appendChild(chip);
        mobileChips.push(chip);
      }
    }

    const drawCurve = (activeIdx: number) => {
      ctx.clearRect(0, 0, 210, h);
      ctx.beginPath();
      ctx.moveTo(P0X, P0Y);
      ctx.quadraticCurveTo(P1X, P1Y, P2X, P2Y);
      ctx.strokeStyle = "rgba(0,0,0,0.07)";
      ctx.lineWidth = 1;
      ctx.stroke();

      if (activeIdx >= 0 && activeIdx < SECTIONS.length) {
        let curLen = 0, px = P0X, py = P0Y;
        ctx.beginPath();
        ctx.moveTo(P0X, P0Y);
        for (let i = 1; i <= 200; i++) {
          const t = i / 200;
          const nx = qBez(t, P0X, P1X, P2X), ny = qBez(t, P0Y, P1Y, P2Y);
          curLen += Math.sqrt((nx - px) ** 2 + (ny - py) ** 2);
          ctx.lineTo(nx, ny);
          px = nx; py = ny;
          if (curLen >= totalLen * (activeIdx / Math.max(SECTIONS.length - 1, 1))) break;
        }
        const isTurning = SECTIONS[activeIdx].turning;
        ctx.strokeStyle = isTurning ? "rgba(99,102,241,0.35)" : "rgba(16,185,129,0.35)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    };

    drawCurve(-1);

    // Scroll handler
    const sectionEls = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean) as HTMLElement[];

    const onScroll = () => {
      let current = -1;
      for (let i = 0; i < sectionEls.length; i++) {
        if (window.scrollY >= sectionEls[i].offsetTop - window.innerHeight * 0.35) current = i;
      }
      if (current !== activeIndexRef.current) {
        activeIndexRef.current = current;
        elNodes.forEach((n, j) => n.classList.toggle("active", j === current));
        mobileChips.forEach((c, j) => c.classList.toggle("active", j === current));
        drawCurve(current);
      }
      const heroBottom = heroEl.offsetTop + heroEl.offsetHeight - 150;
      const pastHero = window.scrollY > heroBottom;
      const beforeCta = ctaEl.getBoundingClientRect().top > headerHeight;
      const showSectionNav = pastHero && beforeCta;
      wheelNav.classList.toggle("visible", showSectionNav);
      mobileNav.classList.toggle("visible", showSectionNav);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    const cleanup = initWheel();
    const onResize = () => { initWheel(); };
    window.addEventListener("resize", onResize);
    return () => {
      cleanup?.();
      window.removeEventListener("resize", onResize);
    };
  }, [initWheel]);

  /* ══════════════════════════════════════ */
  /* ══  RENDER                         ══ */
  /* ══════════════════════════════════════ */
  return (
    <div className="research-page">
      {/* Wheel nav (desktop) */}
      <div ref={wheelNavRef} className="rp-wheel-nav">
        <canvas ref={canvasRef} className="rp-wheel-canvas" />
        <div ref={nodesRef} className="rp-wheel-nodes" />
      </div>

      {/* Mobile nav */}
      <div ref={mobileNavRef} className="rp-mobile-nav">
        <div className="rp-mobile-nav-inner" />
      </div>

      {/* ── Opening ── */}
      <section ref={heroRef} id="rp-hero" style={{ minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 1.5rem", position: "relative" }} className="rp-content-section" >
        <div style={{ maxWidth: 840 }}>
          <p className="rp-opening-line rp-ol1" style={{ fontSize: "clamp(26px, 4.5vw, 50px)", fontWeight: 500, lineHeight: 1.15, letterSpacing: "-0.025em", color: "#18181b" }}>
            Your support team resolves <span className="rp-data-hl">11,200</span> tickets per month.
          </p>
          <p className="rp-opening-line rp-ol2" style={{ fontSize: "clamp(26px, 4.5vw, 50px)", fontWeight: 500, lineHeight: 1.15, letterSpacing: "-0.025em", marginTop: 16 }}>
            <span style={{ color: "#a1a1aa" }}>73% of them</span> are questions you've already answered.
          </p>
          <p className="rp-opening-line rp-ol3" style={{ fontSize: "clamp(14px, 1.3vw, 18px)", color: "#a1a1aa", fontWeight: 300, lineHeight: 1.7, marginTop: 32, maxWidth: 560 }}>
            A deep dive into why customer support is structurally broken, what the data actually shows, and the infrastructure that fixes it.
          </p>
        </div>
        <div className="rp-opening-scroll" style={{ position: "absolute", bottom: 40, left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.15em", color: "#a1a1aa", textTransform: "uppercase" }}>Scroll to read</span>
          <ArrowDown size={14} className="rp-scroll-bounce" style={{ color: "#a1a1aa" }} />
        </div>
      </section>

      {/* ── §01: Economics ── */}
      <section id="rp-s1" className="rp-content-section">
        <div style={{ maxWidth: 768 }}>
          <div className="rp-reveal">
            <span className="rp-chapter-label">§01</span>
            <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 500, letterSpacing: "-0.025em", marginTop: 16, marginBottom: 32, color: "#18181b" }}>The Economics of a Support Ticket</h2>
          </div>
          <div className="rp-reveal rp-reveal-d1" style={{ display: "flex", flexDirection: "column", gap: 24, color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15 }}>
            <p>The average cost of a single support interaction across SaaS companies is <span className="rp-data-hl" style={{ color: "#18181b" }}>$15.42</span>.
              <FootnoteRef text="Gartner, 2024. 'State of Customer Service Operations.' Survey of 2,400 SaaS companies with 50–5,000 employees.">[1]</FootnoteRef>
              {" "}This figure accounts for agent compensation, tooling overhead, management, and the implicit cost of the customer's time. It does not account for what happens after the ticket closes.
            </p>
            <p>A company handling 11,200 tickets per month spends approximately <span className="rp-data-hl" style={{ color: "#18181b" }}>$2.07M annually</span> on support operations. For a Series B company with $8M ARR, that's 26% of revenue redirected toward answering questions that, in most cases, already exist somewhere in a knowledge base, a past conversation, or a product walkthrough.</p>
            <p>But the direct cost is the smaller problem.</p>
          </div>
          <div className="rp-reveal rp-reveal-d2 rp-key-insight">
            <div className="rp-key-insight-label">Key Insight</div>
            <p style={{ color: "#3f3f46", fontWeight: 300, lineHeight: 1.7, fontSize: 15 }}>A customer who experiences a support resolution time exceeding 4 hours has a <span style={{ color: "#18181b", fontWeight: 500 }}>3.4× higher probability of churning</span> within 90 days than one resolved in under 30 minutes. The ticket cost is $15. The churn cost is $1,200–$4,800 in lifetime value.
              <FootnoteRef text="Harvard Business Review, 2023. 'The Hidden Cost of Slow Support.' Longitudinal study of 18,000 SaaS customers across 120 companies.">[2]</FootnoteRef>
            </p>
          </div>
          <div className="rp-reveal rp-reveal-d3" style={{ display: "flex", flexDirection: "column", gap: 24, color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15 }}>
            <p>The real economics of support aren't about the cost of answering. They're about the cost of not answering fast enough, accurately enough, or at 3 AM when your team is asleep.</p>
            <p>This isn't a staffing problem. Companies that doubled their support headcount between 2021 and 2023 saw only an <span className="rp-data-hl" style={{ color: "#18181b" }}>11% improvement</span> in resolution times.
              <FootnoteRef text="Zendesk Benchmark Report, 2024. Data from 45,000 companies using Zendesk Suite.">[3]</FootnoteRef>
              {" "}The problem is structural.
            </p>
          </div>
        </div>
      </section>

      {/* ── §02: Documentation Paradox ── */}
      <section id="rp-s2" className="rp-content-section">
        <div style={{ maxWidth: 768 }}>
          <div className="rp-reveal">
            <div className="rp-section-divider" style={{ marginBottom: 32 }} />
            <span className="rp-chapter-label">§02</span>
            <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 500, letterSpacing: "-0.025em", marginTop: 16, marginBottom: 32, color: "#18181b" }}>The Documentation Paradox</h2>
          </div>
          <div className="rp-reveal rp-reveal-d1" style={{ display: "flex", flexDirection: "column", gap: 24, color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15 }}>
            <p>Most companies have a knowledge base. The average SaaS product has <span className="rp-data-hl" style={{ color: "#18181b" }}>347 articles</span> in their help center.
              <FootnoteRef text="Document360 Internal Data, 2024. Aggregated from 12,000 knowledge bases on the platform.">[4]</FootnoteRef>
              {" "}They've invested months writing, organizing, and maintaining documentation. The assumption is straightforward: if the answer exists, the customer will find it.
            </p>
            <p>They don't. Here's where customers actually look for answers first:</p>
          </div>
          <div className="rp-reveal rp-reveal-d2" style={{ marginTop: 40, display: "flex", flexDirection: "column", gap: 20 }}>
            <Bar label="Google search" value="64%" />
            <Bar label="Ask a colleague or friend" value="41%" />
            <Bar label="Contact support directly" value="38%" />
            <Bar label="Search your knowledge base" value="23%" green />
            <Bar label="In-app guidance / tooltips" value="12%" />
            <p style={{ fontSize: 11, color: "#a1a1aa", fontFamily: "'JetBrains Mono', monospace", marginTop: 12 }}>Source: Intercom Customer Behavior Report, 2024. n=8,200. Multiple selections allowed.</p>
          </div>
          <div className="rp-reveal rp-reveal-d3" style={{ display: "flex", flexDirection: "column", gap: 24, color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15, marginTop: 40 }}>
            <p>The knowledge base — the thing you spent months building — ranks fourth. Not because it lacks content, but because the retrieval mechanism is broken. Traditional search relies on keyword matching. A customer types "how to connect my CRM" but your article is titled "Integration Setup for Third-Party Data Sources." The keywords don't overlap. The search fails. A ticket is created.</p>
          </div>
          <div className="rp-reveal rp-reveal-d4 rp-key-insight">
            <div className="rp-key-insight-label">Key Insight</div>
            <p style={{ color: "#3f3f46", fontWeight: 300, lineHeight: 1.7, fontSize: 15 }}>
              <span style={{ color: "#18181b", fontWeight: 500 }}>73% of support tickets</span> contain questions whose answers already exist in company documentation. The bottleneck isn't knowledge creation — it's knowledge retrieval.
            </p>
          </div>
          <div className="rp-reveal rp-reveal-d5" style={{ display: "flex", flexDirection: "column", gap: 24, color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15 }}>
            <p>This is a retrieval problem, not a content problem. And retrieval problems are exactly the class of problems that large language models were architecturally designed to solve — but not in the way most people think.</p>
          </div>
        </div>
      </section>

      {/* ── §03: Resolution Gap ── */}
      <section id="rp-s3" className="rp-content-section">
        <div style={{ maxWidth: 768 }}>
          <div className="rp-reveal">
            <div className="rp-section-divider" style={{ marginBottom: 32 }} />
            <span className="rp-chapter-label">§03</span>
            <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 500, letterSpacing: "-0.025em", marginTop: 16, marginBottom: 32, color: "#18181b" }}>The Resolution Gap</h2>
          </div>
          <div className="rp-reveal rp-reveal-d1" style={{ display: "flex", flexDirection: "column", gap: 24, color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15 }}>
            <p>First-contact resolution (FCR) is the single most correlated metric with customer satisfaction.
              <FootnoteRef text="SQM Group, 2023. 'Customer Satisfaction and First Contact Resolution.' Study of 500 North American contact centers.">[5]</FootnoteRef>
              {" "}When a customer's issue is resolved in their first interaction, satisfaction scores average 4.6/5. When it requires a second interaction, that drops to 3.1. After three or more, it falls below 2.0.
            </p>
            <p>The industry average FCR rate is <span className="rp-data-hl" style={{ color: "#18181b" }}>34%</span>.
              <FootnoteRef text="ICMI, 2024. 'Key Contact Center Metrics.' Annual benchmarking report.">[6]</FootnoteRef>
              {" "}That means two out of three customers who contact support will need to reach out again about the same issue.
            </p>
          </div>
          <div className="rp-reveal rp-reveal-d2 rp-pull-quote">
            The average escalated support ticket takes <span style={{ color: "#18181b", fontStyle: "normal", fontWeight: 500 }}>47 hours</span> to fully resolve. During that time, the customer's perception of your product degrades continuously.
          </div>
          <div className="rp-reveal rp-reveal-d3" style={{ display: "flex", flexDirection: "column", gap: 24, color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15 }}>
            <p>The resolution gap isn't a failure of individual agents. It's a failure of the system. Agents spend <span className="rp-data-hl" style={{ color: "#18181b" }}>62% of their time</span> on internal tool navigation, context gathering, and handoff coordination.
              <FootnoteRef text="McKinsey, 2024. 'The Agent Experience Gap.' Time-motion study of 1,200 support agents.">[7]</FootnoteRef>
              {" "}Only 38% is spent actually solving the problem.
            </p>
            <p>The system isn't broken because people are bad at their jobs. It's broken because the infrastructure underneath them was built for a different era.</p>
          </div>
          <div className="rp-reveal rp-reveal-d4" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginTop: 48 }}>
            <div className="rp-metric-card" style={{ textAlign: "center" }}>
              <div style={{ fontSize: 24, fontWeight: 500, color: "#27272a" }}>34%</div>
              <div style={{ fontSize: 12, color: "#a1a1aa", marginTop: 8, fontWeight: 300 }}>Average first-contact<br />resolution rate</div>
            </div>
            <div className="rp-metric-card" style={{ textAlign: "center" }}>
              <div style={{ fontSize: 24, fontWeight: 500, color: "#27272a" }}>47 hrs</div>
              <div style={{ fontSize: 12, color: "#a1a1aa", marginTop: 8, fontWeight: 300 }}>Average time to resolve<br />an escalated ticket</div>
            </div>
            <div className="rp-metric-card" style={{ textAlign: "center" }}>
              <div style={{ fontSize: 24, fontWeight: 500, color: "#27272a" }}>62%</div>
              <div style={{ fontSize: 12, color: "#a1a1aa", marginTop: 8, fontWeight: 300 }}>Agent time spent on<br />non-resolution tasks</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── §04: Not a Chatbot ── */}
      <section id="rp-s4" className="rp-content-section" style={{ position: "relative" }}>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, transparent, rgba(236,253,245,0.5), transparent)", pointerEvents: "none" }} />
        <div style={{ maxWidth: 768, position: "relative" }}>
          <div className="rp-reveal">
            <div className="rp-section-divider" style={{ marginBottom: 32 }} />
            <span className="rp-chapter-label" style={{ color: "#059669" }}>§04</span>
            <h2 style={{ fontSize: "clamp(24px, 3vw, 44px)", fontWeight: 500, letterSpacing: "-0.025em", marginTop: 16, marginBottom: 32, color: "#18181b" }}>
              Not a Chatbot.<br /><span style={{ color: "#059669" }}>Infrastructure.</span>
            </h2>
          </div>
          <div className="rp-reveal rp-reveal-d1" style={{ display: "flex", flexDirection: "column", gap: 24, color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15 }}>
            <p>Most companies approaching AI for support start from the wrong end. They think: "We need a chatbot." They evaluate tools based on how the chat widget looks, what the conversational flow feels like, whether it can handle small talk.</p>
            <p>This is like choosing a database by evaluating the color of the admin panel.</p>
            <p>The companies seeing transformative results didn't start with a chatbot. They started with infrastructure — a system that ingests everything the company knows, processes it into a format that machines can reason over, retrieves the right information for any given question, and generates responses that are accurate, contextual, and on-brand.</p>
          </div>
          <div className="rp-reveal rp-reveal-d2 rp-def-box">
            <div className="rp-def-label">Definition</div>
            <p style={{ color: "#3f3f46", fontWeight: 300, lineHeight: 1.7, fontSize: 15 }}>
              <span style={{ color: "#18181b", fontWeight: 500 }}>Retrieval-Augmented Generation (RAG)</span> is an architecture that combines a retrieval system with a generative model. Unlike fine-tuning, which bakes knowledge into model weights, RAG retrieves in real-time — meaning the knowledge base can be updated without retraining.
            </p>
          </div>
          <div className="rp-reveal rp-reveal-d3" style={{ display: "flex", flexDirection: "column", gap: 24, color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15 }}>
            <p>Here's why this matters: your product changes every week. A fine-tuned model captures knowledge at a point in time. A RAG system captures knowledge as it exists <em>right now</em>.</p>
            <p>When a customer asks "Why isn't my webhook firing after the latest update?", the system needs to:</p>
          </div>
          <div className="rp-reveal rp-reveal-d4" style={{ display: "flex", flexDirection: "column", gap: 16, marginLeft: 16, borderLeft: "1px solid #e4e4e7", paddingLeft: 24 }}>
            {[
              "Understand that 'webhook' and 'latest update' refer to specific technical entities in your product",
              "Locate the changelog entry for the most recent update that mentions webhooks",
              "Cross-reference with known issues, configuration docs, and past support conversations",
              "Synthesize a response that addresses this specific customer's configuration",
            ].map((text, i) => (

              <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ color: "#059669", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, marginTop: 4, flexShrink: 0 }}>0{i + 1}</span>
                <p style={{ color: "#71717a", fontWeight: 300, fontSize: 15, lineHeight: 1.7, margin: 0 }}>{text}</p>
              </div>
            ))}
          </div>
          <div className="rp-reveal rp-reveal-d5" style={{ display: "flex", flexDirection: "column", gap: 24, color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15, marginTop: 32 }}>
            <p>Steps 1–3 are retrieval. Step 4 is generation. The quality of the final response is almost entirely determined by the quality of steps 1–3. This is the layer that most "AI chatbot" products skip. They plug a generic LLM into basic vector search and call it done. The results are predictably mediocre.</p>
          </div>
        </div>
      </section>

      {/* ── §05: SmushLabs ── */}
      <section id="rp-s5" className="rp-content-section">
        <div style={{ maxWidth: 768 }}>
          <div className="rp-reveal">
            <div className="rp-section-divider" style={{ marginBottom: 32 }} />
            <span className="rp-chapter-label">§05</span>
            <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 500, letterSpacing: "-0.025em", marginTop: 16, marginBottom: 16, color: "#18181b" }}>SmushLabs</h2>
            <p style={{ color: "#a1a1aa", fontWeight: 300, fontSize: 18, marginBottom: 48 }}>We built the infrastructure described in the previous section.</p>
          </div>
          <div className="rp-reveal rp-reveal-d1" style={{ display: "flex", flexDirection: "column", gap: 24, color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15 }}>
            <p>SmushLabs is an AI support infrastructure layer. It sits between your company's knowledge and your customer's question, and it makes the retrieval and generation pipeline work in a way that's accurate, fast, and controllable.</p>
            <p>We don't describe it as a chatbot because that frames the product around the interface rather than the system. The chat widget is one output channel. The same infrastructure powers email responses, Slack integrations, API endpoints, and agent-assist tools.</p>
            <p>The design principles that govern the system:</p>
          </div>
          <div className="rp-reveal rp-reveal-d2" style={{ marginTop: 40 }}>
            {[
              { title: "Retrieval first", desc: "Every response starts with finding the right information. We invest more engineering effort in retrieval than generation. Better retrieval means fewer hallucinations." },
              { title: "Knowledge as a live system", desc: "Your knowledge base is a living system that ingests docs, logs, changelogs, API specs, and wikis — keeping the index current as sources change." },
              { title: "Accuracy over fluency", desc: 'An inarticulate but correct answer always beats a beautifully written wrong one. The system says "I don\'t know" rather than guess. Confidence scoring on every response.' },
              { title: "Human in the loop", desc: "When confidence is low — or the query involves billing, deletion, or escalation — a human takes over with full context of what the AI already discussed." },
              { title: "Observability by default", desc: "Every response is traceable. See what sources were retrieved, why a particular answer was generated, and where confidence broke down. No black boxes." },
              { title: "Channel-agnostic core", desc: "Intelligence decoupled from delivery. Web widget, API, email, Slack — the same brain, different mouths." },
            ].map((item, i, arr) => (
              <div key={i} style={{ borderTop: "1px solid #e4e4e7", borderBottom: i === arr.length - 1 ? "1px solid #e4e4e7" : "none", padding: "24px 0", display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16 }}>
                <span style={{ color: "#18181b", fontWeight: 500, fontSize: 14 }}>{item.title}</span>
                <span style={{ color: "#71717a", fontWeight: 300, fontSize: 15, lineHeight: 1.7 }}>{item.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── §06: Architecture ── */}
      <section id="rp-s6" className="rp-content-section">
        <div style={{ maxWidth: 768 }}>
          <div className="rp-reveal">
            <div className="rp-section-divider" style={{ marginBottom: 32 }} />
            <span className="rp-chapter-label">§06</span>
            <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 500, letterSpacing: "-0.025em", marginTop: 16, marginBottom: 32, color: "#18181b" }}>The Architecture</h2>
          </div>
          <div className="rp-reveal rp-reveal-d1" style={{ color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15 }}>
            <p>The system is structured as five sequential layers. Understanding this stack is useful for evaluating any AI support product — not just SmushLabs.</p>
          </div>
          <div className="rp-reveal rp-reveal-d2" style={{ marginTop: 48, display: "flex", flexDirection: "column", gap: 12 }}>
            {[
              { num: "5", label: "Delivery Layer", desc: "Response formatting, channel-specific rendering, rate limiting. Output adapts to destination — widget, API, or email draft.", color: "#6366f1", bg: "#eef2ff", border: "#c7d2fe" },
              { num: "4", label: "Generation Layer", desc: "Takes retrieved context and query, produces a response under strict constraints: tone, length, source citation, confidence threshold.", color: "#059669", bg: "#ecfdf5", border: "#a7f3d0" },
              { num: "3", label: "Retrieval Layer", desc: "Query embedding, vector matching, query decomposition, re-ranking by relevance/recency/authority, contextual compression.", color: "#059669", bg: "#ecfdf5", border: "#a7f3d0" },
              { num: "2", label: "Knowledge Layer", desc: "Vector index organized by source type, recency, metadata. Chunking preserves structure — code blocks, tables, headings stay intact.", color: "#059669", bg: "#ecfdf5", border: "#a7f3d0" },
              { num: "1", label: "Ingestion Layer", desc: "URLs, files (PDF, DOCX, MD), APIs (Zendesk, Intercom, Notion, Confluence), raw text. Cleaned, deduplicated, metadata-enriched, scheduled re-ingestion.", color: "#71717a", bg: "#f4f4f5", border: "#e4e4e7" },
            ].map((layer, i, arr) => (
              <div key={i}>
                <div className="rp-arch-layer" style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 6, background: layer.bg, border: `1px solid ${layer.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: layer.color }}>{layer.num}</span>
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: "#18181b", marginBottom: 4 }}>{layer.label}</div>
                    <div style={{ fontSize: 13, color: "#71717a", fontWeight: 300, lineHeight: 1.7 }}>{layer.desc}</div>
                  </div>
                </div>
                {i < arr.length - 1 && (
                  <div style={{ display: "flex", justifyContent: "center", padding: "4px 0" }}>
                    <ArrowDown size={12} style={{ color: "#d4d4d8" }} />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="rp-reveal rp-reveal-d3" style={{ display: "flex", flexDirection: "column", gap: 24, color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15, marginTop: 48 }}>
            <p>Layers 2 and 3 — knowledge and retrieval — determine the ceiling of the entire system. A perfect generation layer cannot compensate for poor retrieval. This is why we've invested disproportionately in ingestion quality, chunking strategies, and re-ranking algorithms.</p>
            <p>There's also a <span style={{ color: "#18181b", fontWeight: 500 }}>feedback loop</span>: every resolved conversation feeds back into the knowledge layer. If an agent resolves a novel issue, that conversation is indexed and becomes retrievable. The system gets better from the natural flow of operations.</p>
          </div>
        </div>
      </section>

      {/* ── §07: Capabilities ── */}
      <section id="rp-s7" className="rp-content-section">
        <div style={{ maxWidth: 768 }}>
          <div className="rp-reveal">
            <div className="rp-section-divider" style={{ marginBottom: 32 }} />
            <span className="rp-chapter-label">§07</span>
            <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 500, letterSpacing: "-0.025em", marginTop: 16, marginBottom: 32, color: "#18181b" }}>Capabilities</h2>
          </div>
          <div className="rp-reveal rp-reveal-d1" style={{ color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15 }}>
            <p>What follows is not a feature list. It's an explanation of what the architecture enables in practice, and why each capability matters for your support economics.</p>
          </div>
          {[
            { icon: <Layers size={14} />, title: "Multi-Source Knowledge Ingestion", body: "Ingests from URLs, uploaded files, API integrations (Zendesk, Intercom, Notion, Confluence, Gitbook), and raw text. Format-specific parsers for each source.", why: "Most teams have knowledge scattered across 6–12 tools. Multi-source ingestion means you deploy in hours, not quarters." },
            { icon: <GitBranch size={14} />, title: "Intelligent Escalation with Full Context", body: "When confidence drops below threshold — or triggers match (billing, deletion, legal) — conversation routes to a human with full history, sources, scores, and a draft response.", why: 'The #1 complaint about AI support is the "context reset." Full-context handoff eliminates it.' },
            { icon: <BarChart3 size={14} />, title: "Insight Extraction from Conversation Data", body: 'Every conversation generates structured metadata: topic clusters, sentiment, resolution outcomes. Dashboard answers: "What drives escalations this week?" "Which articles are referenced but still generate follow-ups?"', why: "Support conversations are the largest dataset of customer pain points that most companies ignore." },
            { icon: <RefreshCw size={14} />, title: "Continuous Learning Without Retraining", body: "New knowledge is available immediately — no retraining. A help article published at 2 PM is referenceable by 2:05 PM. Resolved conversations auto-index, creating a compounding effect.", why: "Traditional models degrade as the product changes. RAG systems improve as knowledge grows." },
            { icon: <ShieldCheck size={14} />, title: "Response Governance", body: "Configurable guardrails: tone, topic boundaries, length limits, disclaimers, PII detection. Every response includes source citations.", why: "Fear of AI saying something wrong is the #1 adoption blocker. Governance is the prerequisite for deployment." },
          ].map((cap, i) => (
            <div key={i} className="rp-reveal" style={{ marginTop: 56 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <div style={{ width: 24, height: 24, borderRadius: 4, background: "#ecfdf5", display: "flex", alignItems: "center", justifyContent: "center", color: "#059669" }}>{cap.icon}</div>
                <h3 style={{ fontSize: 18, fontWeight: 500, color: "#18181b", margin: 0 }}>{cap.title}</h3>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16, color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15 }}>
                <p style={{ margin: 0 }}>{cap.body}</p>
                <p style={{ margin: 0 }}><span style={{ color: "#3f3f46" }}>Why it matters:</span> {cap.why}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── §08: Evidence ── */}
      <section id="rp-s8" className="rp-content-section" style={{ position: "relative" }}>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, transparent, rgba(238,242,255,0.7), transparent)", pointerEvents: "none" }} />
        <div style={{ maxWidth: 768, position: "relative" }}>
          <div className="rp-reveal">
            <div className="rp-section-divider" style={{ marginBottom: 32 }} />
            <span className="rp-chapter-label">§08</span>
            <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 500, letterSpacing: "-0.025em", marginTop: 16, marginBottom: 32, color: "#18181b" }}>Evidence</h2>
          </div>
          <div className="rp-reveal rp-reveal-d1" style={{ color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15 }}>
            <p>The following metrics are aggregated from companies using SmushLabs in production, measured over 90 days.</p>
          </div>
          <div className="rp-reveal rp-reveal-d2" style={{ marginTop: 48, display: "flex", flexDirection: "column", gap: 20 }}>
            {[
              { label: "First-contact resolution rate", before: "34%", after: "89%", barBefore: 34, barAfter: 55 },
              { label: "Average first-response time", before: "4.2 hrs", after: "12 sec" },
              { label: "Cost per resolved interaction", before: "$15.42", after: "$0.23" },
              { label: "Customer satisfaction (CSAT)", before: "3.2 / 5", after: "4.7 / 5" },
              { label: "Tickets requiring human intervention", before: "100%", after: "18%" },
            ].map((metric, i) => (
              <div key={i} className="rp-metric-card">
                <div style={{ marginBottom: 4 }}>
                  <span style={{ fontSize: 14, color: "#71717a", fontWeight: 300 }}>{metric.label}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ fontSize: 24, fontWeight: 500, color: "#a1a1aa" }}>{metric.before}</div>
                  <ArrowRight size={18} style={{ color: "#d4d4d8" }} />
                  <div style={{ fontSize: 24, fontWeight: 500, color: "#059669" }}>{metric.after}</div>
                </div>
                {metric.barBefore !== undefined && (
                  <div style={{ marginTop: 12, height: 6, borderRadius: 4, background: "#f4f4f5", overflow: "hidden", display: "flex" }}>
                    <div style={{ height: "100%", background: "#d4d4d8", borderRadius: "4px 0 0 4px", width: `${metric.barBefore}%` }} />
                    <div style={{ height: "100%", background: "#10b981", borderRadius: "0 4px 4px 0", width: `${metric.barAfter}%` }} />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="rp-reveal rp-reveal-d3" style={{ marginTop: 32, padding: 16, borderRadius: 8, border: "1px solid #e4e4e7", background: "#fafafa" }}>
            <p style={{ fontSize: 12, color: "#a1a1aa", fontWeight: 300, lineHeight: 1.7, margin: 0, display: "flex", alignItems: "flex-start", gap: 4 }}>
              <Info size={14} style={{ color: "#d4d4d8", flexShrink: 0, marginTop: 2 }} />
              <span><span style={{ color: "#71717a" }}>Context:</span> Median values across 47 B2B SaaS companies (50–2,000 employees), 60+ days production data.</span>
            </p>
          </div>
          <div className="rp-reveal rp-reveal-d4" style={{ display: "flex", flexDirection: "column", gap: 24, color: "#71717a", fontWeight: 300, lineHeight: 1.8, fontSize: 15, marginTop: 40 }}>
            <p>The metric that matters most: <span style={{ color: "#18181b", fontWeight: 500 }}>82% reduction in human-handled tickets</span> doesn't mean 82% fewer agents. It means the same team handles the remaining 18% with more attention, depth, and speed. Good infrastructure doesn't just automate — it elevates what remains.</p>
          </div>
        </div>
      </section>

      {/* ── References ── */}
      <section className="rp-content-section">
        <div style={{ maxWidth: 768 }}>
          <div className="rp-reveal">
            <div className="rp-section-divider" style={{ marginBottom: 32 }} />
            <span className="rp-chapter-label">§09</span>
            <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 500, letterSpacing: "-0.025em", marginTop: 16, marginBottom: 32, color: "#18181b" }}>References</h2>
          </div>
          <div className="rp-reveal rp-reveal-d1" style={{ display: "flex", flexDirection: "column", gap: 20, fontSize: 13, color: "#a1a1aa", fontWeight: 300, lineHeight: 1.7 }}>
            <p><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#71717a" }}>[1]</span> Gartner. (2024). "State of Customer Service Operations." Survey of 2,400 SaaS companies.</p>
            <p><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#71717a" }}>[2]</span> Gallucci, K. & Fernandez, R. (2023). "The Hidden Cost of Slow Support." Harvard Business Review.</p>
            <p><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#71717a" }}>[3]</span> Zendesk. (2024). "Zendesk Benchmark Report." 45,000 companies.</p>
            <p><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#71717a" }}>[4]</span> Document360. (2024). Internal data, 12,000 knowledge bases.</p>
            <p><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#71717a" }}>[5]</span> SQM Group. (2023). "Customer Satisfaction and First Contact Resolution."</p>
            <p><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#71717a" }}>[6]</span> ICMI. (2024). "Key Contact Center Metrics."</p>
            <p><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#71717a" }}>[7]</span> McKinsey & Company. (2024). "The Agent Experience Gap."</p>
          </div>
          <div className="rp-reveal rp-reveal-d2" style={{ marginTop: 56 }}>
            <h3 style={{ fontSize: 14, fontWeight: 500, color: "#3f3f46", marginBottom: 16 }}>Further Reading</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, fontSize: 13 }}>
              {[
                'Lewis, P. et al. (2020). "Retrieval-Augmented Generation for Knowledge-Intensive NLP Tasks." NeurIPS.',
                'Gao, Y. et al. (2024). "RAG for Large Language Models: A Survey." arXiv:2312.10997.',
                'Forrester. (2024). "The Total Economic Impact of AI-Powered Customer Service."',
              ].map((text, i) => (
                <a key={i} href="#" style={{ display: "flex", alignItems: "center", gap: 8, color: "#a1a1aa", textDecoration: "none", transition: "color 0.2s" }}
                  onMouseOver={(e) => (e.currentTarget.style.color = "#3f3f46")}
                  onMouseOut={(e) => (e.currentTarget.style.color = "#a1a1aa")}
                >
                  <ExternalLink size={12} style={{ flexShrink: 0 }} /> {text}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section ref={ctaRef} id="rp-cta" className="rp-content-section rp-cta-section">
        <div className="rp-cta-inner">
          <div className="rp-reveal">
            <p className="rp-cta-eyebrow">If you've read this far, you understand the problem better than most.</p>
            <h2>The infrastructure is ready.</h2>
            <p className="rp-cta-description">See how SmushChat can work with your existing website and business knowledge.</p>
            <div className="rp-cta-actions">
              <Link to="/pricing" className="rp-cta-primary">
                View plans <ArrowRight size={16} />
              </Link>
              <Link to="/#demo" className="rp-cta-secondary">Request a custom demo</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
